import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import { useAuthStore } from '@/stores/auth';
import { useExtensionsStore } from '@/stores/extensions';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { hasPlaceholderPermission, usePermission } from '@/lib/permissions';
import { TimePage } from '@/features/time-tracker/components/time-page';
import { isTimeTabId, type TimeTabId } from '@/features/time-tracker/lib/time-tabs';

type TimeSearchParams = {
  tab?: TimeTabId;
  week?: string;
  /** Which of the two Reports surfaces is showing. */
  view?: 'report' | 'builder';
};

const VALID_VIEWS = new Set(['report', 'builder']);

export const Route = createFileRoute('/time')({
  validateSearch: (search: Record<string, unknown>): TimeSearchParams => ({
    // Only the spelling is validated here; whether the reader may OPEN the tab
    // is the page's call, since that needs permissions the route does not load.
    // The list of names lives with that decision, in `time-tabs.ts` — it used to
    // be spelled out three times, so adding or gating a tab meant remembering
    // all three.
    tab: isTimeTabId(search.tab) ? search.tab : undefined,
    week: typeof search.week === 'string' ? search.week : undefined,
    // Spelled out here on purpose: this function returns a FRESH object, so a
    // param it does not name is dropped on the first navigation — which is why
    // the Report/Builder switch used to be local state that no link could
    // reach and the week stepper wiped.
    view: typeof search.view === 'string' && VALID_VIEWS.has(search.view)
      ? (search.view as TimeSearchParams['view'])
      : undefined,
  }),
  beforeLoad: async () => {
    const { isAuthenticated, isInitialized } = useAuthStore.getState();
    if (!isInitialized) return;
    if (!isAuthenticated) throw redirect({ to: '/login' });

    const user = useAuthStore.getState().user;
    if (hasPlaceholderPermission(user)) throw redirect({ to: '/placeholder' });
  },
  component: TimeRoute,
});

function TimeRoute() {
  const isEnabled = useExtensionsStore((s) => s.isExtensionEnabled('time-tracker'));
  // `time.view` gates GET /time-entries. Same reasoning as the Boards route.
  const canView = usePermission('time.view');
  const { tab, week, view } = Route.useSearch();

  return (
    <AuthGuard>
      <AppLayout>
        {isEnabled && !canView ? (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <h2 className="text-lg font-semibold text-foreground mb-2">You don&rsquo;t have access to the Time Tracker</h2>
            <p className="text-sm text-muted-foreground max-w-md">
              Ask an administrator to grant your role the &ldquo;View time entries&rdquo; permission
              in Settings &rarr; Access Control.
            </p>
          </div>
        ) : isEnabled ? (
          <div className="p-6">
            <TimePage tab={tab} week={week} view={view} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <h2 className="text-lg font-semibold text-foreground mb-2">Time Tracker is not enabled</h2>
            <p className="text-sm text-muted-foreground max-w-md">
              Enable the Time Tracker extension in Settings &rarr; Integrations to start tracking time.
            </p>
          </div>
        )}
      </AppLayout>
    </AuthGuard>
  );
}
